import React, {Component} from "react"
import {withTranslation} from "react-i18next"
import {connect} from "react-redux"
import Moment from "moment"

import employeeService from "../../../../services/Employee.service"
import {formatPercentage} from "../../../../utils/jsUtils"

import {
  DETAIL_COL_LABEL_COLOR,
  DETAIL_COL_LABEL_SIZE,
  DETAIL_COL_VALUE_WEIGHT,
  GenericDetailColumn
} from "./GenericDetailColumn"
import {GenericDetailTitle} from "./GenericDetailTitle"
import AwardInfo from "./AwardInfo"
import {DIALOGS} from "../common";

import {IconPencil} from "@amzn/stencil-react-components/icons"
import {Text, Label} from "@amzn/stencil-react-components/text"
import {Col, Row, Hr, View} from "@amzn/stencil-react-components/layout"
import {Button, ButtonVariant} from "@amzn/stencil-react-components/button"
import {Link} from "@amzn/stencil-react-components/link"
import {Flyout, FlyoutContent} from "@amzn/stencil-react-components/flyout"
import { Select } from '@amzn/stencil-react-components/form'

class UnvestedAwardDetailsDialog extends Component {

  onChangePaymentMethodClick = (vest) => {
    this.props.onClose();
    this.props.onChangePaymentMethodClick(vest);
  }

  onSelectChange = (option) => {
    this.props.onSelectEvent(this.props.events, option.award_id, option.vest_id)
  }

  getTitle(vest, t) {
    if (vest && vest.vested) {
      return t('UnvestedAwardDetailsDialog.TitleVested', {
        award_date: Moment(vest.vest_date).format("ll")});
    }

    return t('UnvestedAwardDetailsDialog.TitleUpcoming', {
      award_date: Moment(vest ? vest.vest_date : this.props.date).format("ll")});
  }

  getPaymentMethodText(vest, t) {
    let fractionalElectionText = "";

    if (this.props.fractionalMode && employeeService.hasFractionalEligibleVest(this.props.employee)){
      fractionalElectionText = " (" + employeeService.getVestFractionalElectionText(t, this.props.employee, vest) + ")";
    }

    return t(`TaxTreatment.${vest.tax_election.code}`) + fractionalElectionText;
  }

  renderVestDetails(isRSU, isOption, vest, t) {
    const optionLabel = t(vest.vested ? 'UnvestedAwardDetailsDialog.OptionsVested' : 'UnvestedAwardDetailsDialog.OptionsVesting');
    const brokerLabels = [t('VestingSummary.TaxBrokerLabel'), t('VestingSummary.NetBrokerLabel')];

    return (
      <Col dataTestId="VestDetails_test_id" gridGap="S200">
        <GenericDetailTitle title={t("UnvestedAwardDetailsDialog.VestDetails")} />
          {isRSU &&
            <GenericDetailColumn
              label={t("UnvestedAwardDetailsDialog.RSUsVesting")}
              value={vest.vest_shares}
            />
          }
          {isOption &&
            <GenericDetailColumn
              label={optionLabel}
              value={vest.vest_shares}
            />
          }
        <Col>
          <Text fontSize={DETAIL_COL_LABEL_SIZE} color={DETAIL_COL_LABEL_COLOR}>
            {t('UnvestedAwardDetailsDialog.BrokersLabel')}
          </Text>
          {this.props.brokers && this.props.brokers.map((broker, i) =>
            <Text key={i} fontWeight={DETAIL_COL_VALUE_WEIGHT}>
              {broker}&nbsp;{this.props.brokers.length > 1 && brokerLabels[i]}
            </Text>
          )}
        </Col>
      </Col>
    )
  }

  renderRsuTaxDetails(vest, t) {
    const vestTaxRate = vest.tax_rate === null ? '' : formatPercentage(this.props.locale, vest.tax_rate, 2, 2);
    const sharesSold = vest.shares_sold === null ? -1 : vest.shares_sold;

    return (
      <Col dataTestId="RsuTaxDetails_test_id" gridGap="S200">
        <GenericDetailTitle title={t("UnvestedAwardDetailsDialog.TaxDetails")} />
        {vestTaxRate &&
          <GenericDetailColumn
            label={t("UnvestedAwardDetailsDialog.TotalTaxRate")}
            value={vestTaxRate}
          />
        }
        <Row>
          <GenericDetailColumn
            label={t("UnvestedAwardDetailsDialog.PaymentMethod")}
            value={this.getPaymentMethodText(vest, t)}
          />
          <View margin={{top: 10, left: 10}}>
            <Button
              icon={<IconPencil title="Change Payment Method Button" color="primary70"/>}
              variant={ButtonVariant.Tertiary}
              onClick={() => this.onChangePaymentMethodClick(vest)}
            />
          </View>
        </Row>
        {sharesSold >= 0 &&
          <GenericDetailColumn
            label={t("UnvestedAwardDetailsDialog.RSUsSold")}
            value={sharesSold}
          />
        }
        <Text fontSize="T50" color="neutral70">* {t("UnvestedAwardDetailsDialog.TaxDetailsNote")}</Text>
      </Col>
    )
  }

  renderOptionBrokerLink(vest, broker, t) {
    if (!broker) {
      return;
    }

    const brokerLabel = vest.vested
      ? t('UnvestedAwardDetailsDialog.OptionVestedLink')
      : t('UnvestedAwardDetailsDialog.OptionUpcomingVestLink');
    const brokerUrl = employeeService.getBrokerUrl(broker, this.props.employee);

    return (
      <Row dataTestId="broker_link_test_id">
        <Text>
          {brokerLabel}
          <Link href={brokerUrl} target="_blank">{broker.name}</Link>
        </Text>
      </Row>
    );
  }

  renderSelect = (t) => {
    if (!this.props.events || this.props.events.length <= 1) {
      return null;
    }

    return (
      <Col>
        <Label>
          {t("UnvestedAwardDetailsDialog.VestEvent")}
        </Label>
        <Select
          dataTestId="Select_VestEvent_test_id"
          options={this.props.events}
          renderOption={this.renderEventLabel}
          renderNativeOption={this.renderEventLabel}
          renderSelectedValue={this.renderEventLabel}
          valueAccessor="award_name"
          onChange={this.onSelectChange}
        />
      </Col>
    )

  }

  renderEventLabel = (event) => {
    return this.props.t("VestingSummary.NextVestEventRow", {award_name: event.award_name, share_quatity: event.share_quantity })
  }

  render() {
    if (!this.props.visible) {
      return null;
    }

    const {t, award, vest} = this.props;
    var isRSU;
    var isOption;

    if (award) {
      isRSU = employeeService.isRSU(award);
      isOption = employeeService.isOption(award);
    }

    return (
      <Flyout dataTestId="UnvestedAwardDetailsDialog_test_id" isOpen={this.props.visible} close={this.props.onClose}>
        <FlyoutContent
          titleText={this.getTitle(vest, t)}
          closeButtonAltText="Close Button For Unvested Award Details Dialog"
          onCloseButtonClick={this.props.onClose}>
          <Col gridGap="S400">
            {this.renderSelect(t)}

            {award && vest &&
              <>
                <AwardInfo
                  award={award}
                  dialogName={DIALOGS.UnvestedAwardDetailsDialog}
                  locale={this.props.locale}
                />
                <Hr/>
                {this.renderVestDetails(isRSU, isOption, vest, t)}
                <Hr/>
                {isRSU && this.renderRsuTaxDetails(vest, t)}
                {isOption && this.renderOptionBrokerLink(vest, this.props.broker, t)}
              </>
            }
          </Col>
        </FlyoutContent>
      </Flyout>
    )
  }
}

const mapState = state => ({
  fractionalMode: state.static_content.fractional_mode,
  employee: state.employee,
  locale: state.employee.language
});

export default connect(mapState)(withTranslation('translationsV2')(UnvestedAwardDetailsDialog));
